import AddIcon from '@mui/icons-material/Add'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloseIcon from '@mui/icons-material/Close'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Button, MenuItem, Select, TextField } from '@mui/material'
import React from 'react'
import { Link } from 'react-router-dom'
import GrantContent from '../../../components/GrantContent'
import { listSearchSx, rowActionSx } from '../../../components/list-styles'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import * as Permisos from '../../../utils/Permisos'
import { EMAIL_TEMPLATES_BACK_PATH, formatCount } from '../common/email-template-common'
import { PAGE_SIZES, useEmailTemplateListModel } from './email-template-list-model'

const SKELETON_ROWS = [1, 2, 3, 4, 5, 6]
const columns = [
    { label: 'Modelo', className: 'min-w-0 md:min-w-[260px]' },
    { label: 'Evento', className: 'hidden whitespace-nowrap md:table-cell' },
    { label: 'Copias', className: 'hidden min-w-[180px] md:table-cell' },
    { label: 'Acciones', className: 'hidden whitespace-nowrap text-right md:table-cell', sr: true },
]

const EmailTemplateList = () => {
    const model = useEmailTemplateListModel()

    const tableHead = (
        <thead>
            <tr className="text-left text-xs font-bold uppercase tracking-[0.05em] text-k-muted">
                {columns.map((column) => (
                    <th
                        key={column.label}
                        className={`sticky top-0 z-10 bg-k-surface px-2.5 py-3 font-bold ${column.className} ${column.label === 'Modelo' ? 'pl-4' : ''} ${column.sr ? 'pr-4 text-right' : ''}`}
                    >
                        {column.sr ? <span className="sr-only">{column.label}</span> : column.label}
                    </th>
                ))}
            </tr>
        </thead>
    )

    return (
        <KarteraAdminTemplate loading={false} padded={false} title="Modelos de correo">
            <div className="flex min-h-0 flex-1 flex-col gap-[18px] bg-k-bg px-[clamp(16px,2.5vw,32px)] pb-10 pt-6">
                {model.loading && !model.initialLoadDone && (
                    <div className="flex min-h-0 flex-1 flex-col gap-[18px]">
                        <div>
                            <div className="mb-2 h-7 w-48 animate-pulse rounded bg-k-line md:h-8 md:w-64" />
                            <div className="hidden h-4 w-64 animate-pulse rounded bg-k-line md:block" />
                        </div>
                        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[18px] border border-k-line bg-k-surface">
                            <div className="shrink-0 border-b border-k-line px-4 py-3.5">
                                <div className="h-[42px] max-w-[380px] animate-pulse rounded-xl bg-k-line" />
                            </div>
                            <div className="min-h-0 flex-1 overflow-auto">
                                <table className="w-full table-fixed border-collapse md:min-w-[760px] md:table-auto">
                                    {tableHead}
                                    <tbody className="bg-k-surface">
                                        {SKELETON_ROWS.map((row) => (
                                            <tr key={row} className="border-t border-k-line">
                                                <td className="px-4 py-2.5">
                                                    <div className="flex items-center gap-3">
                                                        <div className="h-[34px] w-[34px] animate-pulse rounded-[10px] bg-k-line" />
                                                        <div>
                                                            <div className="h-3.5 w-40 animate-pulse rounded bg-k-line" />
                                                            <div className="mt-1 h-3 w-56 animate-pulse rounded bg-k-line" />
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="hidden px-2.5 py-2.5 md:table-cell"><div className="h-5 w-32 animate-pulse rounded-full bg-k-line" /></td>
                                                <td className="hidden px-2.5 py-2.5 md:table-cell"><div className="h-3.5 w-36 animate-pulse rounded bg-k-line" /></td>
                                                <td className="hidden px-4 py-2.5 text-right md:table-cell"><div className="ml-auto h-9 w-16 animate-pulse rounded-[10px] bg-k-line" /></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {model.error && !model.loading && (
                    <div className="mb-6 rounded-lg border border-k-danger/30 bg-k-danger-soft p-4">
                        <div className="flex items-center">
                            <ErrorOutlineIcon className="mr-2 text-k-danger" style={{ fontSize: 20 }} />
                            <p className="text-sm text-k-danger-ink">{model.error}</p>
                        </div>
                        <Button variant="contained" onClick={model.reload} className="mt-3 !bg-k-danger !text-white">
                            Reintentar
                        </Button>
                    </div>
                )}

                {model.initialLoadDone && !model.error && (
                    <div className="flex min-h-0 flex-1 flex-col gap-[18px]">
                        <header className="flex shrink-0 flex-wrap items-center justify-between gap-3.5 md:items-end">
                            <div className="flex flex-col gap-1">
                                <Link to={EMAIL_TEMPLATES_BACK_PATH} className="inline-flex min-h-7 items-center gap-1 self-start text-[13px] text-k-muted hover:text-brand-indigo">
                                    <ChevronLeftIcon style={{ fontSize: 16 }} />
                                    Configuración
                                </Link>
                                <h1 className="m-0 text-xl font-extrabold tracking-[-0.03em] text-k-ink md:text-[28px]">Modelos de correo</h1>
                                <p className="m-0 hidden text-sm text-k-muted md:block">Plantillas de correos automáticos · {formatCount(model.totalItems)} registros</p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2.5">
                                <button
                                    type="button"
                                    aria-label="Actualizar"
                                    title="Actualizar"
                                    className="inline-flex h-11 w-11 items-center justify-center gap-2 rounded-xl border border-k-line bg-k-surface text-sm font-semibold text-k-ink hover:bg-k-hover md:w-auto md:px-4"
                                    onClick={model.reload}
                                >
                                    <RefreshIcon className={model.loading ? 'animate-spin' : ''} style={{ fontSize: 18 }} />
                                    <span className="sr-only md:not-sr-only">Actualizar</span>
                                </button>
                                <GrantContent permisos={[Permisos.COMUNICADOS_W]}>
                                    <Link
                                        to={model.newLocation.pathname}
                                        state={model.newLocation.state}
                                        aria-label="Nuevo modelo"
                                        title="Nuevo modelo"
                                        className="inline-flex h-11 w-11 items-center justify-center gap-2 rounded-xl bg-k-primary text-sm font-bold !text-white no-underline hover:brightness-105 hover:!text-white md:w-auto md:px-[18px]"
                                    >
                                        <AddIcon style={{ fontSize: 16 }} />
                                        <span className="sr-only md:not-sr-only">Nuevo modelo</span>
                                    </Link>
                                </GrantContent>
                            </div>
                        </header>

                        <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[18px] border border-k-line bg-k-surface">
                            <div className="flex shrink-0 items-start gap-2.5 border-b border-k-line px-4 py-3.5">
                                <div className="min-w-0 flex-1">
                                    <TextField
                                        id="filterModelosQ"
                                        label="Buscar por nombre, asunto o evento"
                                        variant="outlined"
                                        size="small"
                                        value={model.search}
                                        slotProps={{ htmlInput: { autoComplete: 'off' } }}
                                        sx={listSearchSx}
                                        onChange={(event) => model.changeSearch(event.target.value)}
                                    />
                                </div>
                                <button
                                    type="button"
                                    title="Limpiar filtros"
                                    aria-label="Limpiar filtros"
                                    className="inline-flex h-10 w-10 shrink-0 items-center justify-center self-start rounded-xl bg-k-danger-soft text-k-danger-ink hover:brightness-95"
                                    onClick={model.clearSearch}
                                >
                                    <CloseIcon style={{ fontSize: 18 }} />
                                </button>
                            </div>

                            <div className={`min-h-0 flex-1 overflow-auto ${model.loading ? 'opacity-60' : ''}`}>
                                <table className="w-full table-fixed border-collapse text-sm md:min-w-[760px] md:table-auto">
                                    {tableHead}
                                    <tbody className="bg-k-surface">
                                        {model.rows.length === 0 && (
                                            <tr>
                                                <td className="px-4 py-12 text-center text-sm text-k-muted md:hidden">
                                                    {model.search ? 'No se encontraron modelos con la búsqueda aplicada.' : 'Aún no tienes modelos de correo.'}
                                                </td>
                                                <td colSpan={columns.length} className="hidden px-4 py-12 text-center text-sm text-k-muted md:table-cell">
                                                    {model.search ? 'No se encontraron modelos con la búsqueda aplicada.' : 'Aún no tienes modelos de correo.'}
                                                </td>
                                            </tr>
                                        )}
                                        {model.rows.map((row) => (
                                            <tr
                                                key={row.id}
                                                className="cursor-pointer border-t border-k-line transition-colors hover:bg-k-hover"
                                                onClick={() => model.openTemplate(row.id)}
                                            >
                                                <td className="max-w-[360px] px-4 py-2.5">
                                                    <div className="flex min-w-0 items-center gap-3 text-left">
                                                        <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[10px] bg-tone-blue text-tone-blue-ink">
                                                            <EmailOutlinedIcon style={{ fontSize: 18 }} />
                                                        </span>
                                                        <span className="flex min-w-0 flex-col">
                                                            <span className="truncate font-bold text-k-ink">{row.name || '—'}</span>
                                                            <span className={`truncate text-xs ${row.subject ? 'text-k-ink-2' : 'italic text-k-muted'}`}>
                                                                {row.subject ? `Asunto: ${row.subject}` : 'Sin asunto'}
                                                            </span>
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="hidden whitespace-nowrap px-2.5 py-2.5 md:table-cell">
                                                    <span className={`rounded-full px-[9px] py-[3px] text-xs font-bold ${row.eventTone}`}>{row.event}</span>
                                                </td>
                                                <td className="hidden px-2.5 py-2.5 md:table-cell">
                                                    <span className="flex min-w-0 flex-col">
                                                        <span className={`truncate ${row.cc ? 'text-k-ink-2' : 'italic text-k-muted'}`}>{row.cc ? `CC: ${row.cc}` : 'Sin copia'}</span>
                                                        <span className={`truncate text-xs ${row.bcc ? 'text-k-muted' : 'italic text-k-muted'}`}>{row.bcc ? `CCO: ${row.bcc}` : 'Sin copia oculta'}</span>
                                                    </span>
                                                </td>
                                                <td className="hidden whitespace-nowrap px-4 py-2.5 text-right md:table-cell">
                                                    <Button
                                                        variant="outlined"
                                                        aria-label={`Editar ${row.name || 'modelo'}`}
                                                        sx={rowActionSx}
                                                        onClick={(event) => {
                                                            event.stopPropagation()
                                                            model.openTemplate(row.id)
                                                        }}
                                                    >
                                                        Editar
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-t border-k-line bg-k-surface px-4 py-3 text-sm text-k-muted">
                                <span className="hidden md:inline">Mostrando {formatCount(model.rangeFrom)} a {formatCount(model.rangeTo)} de {formatCount(model.totalItems)} modelos</span>
                                <div className="flex items-center gap-1">
                                    <button
                                        type="button"
                                        aria-label="Página anterior"
                                        disabled={model.page <= 1 || model.loading}
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-k-line bg-k-surface text-k-ink-2 hover:bg-k-hover disabled:opacity-40"
                                        onClick={() => model.setPage(model.page - 1)}
                                    >
                                        <ChevronLeftIcon fontSize="small" />
                                    </button>
                                    {model.pages.map((number) => (
                                        <button
                                            key={number}
                                            type="button"
                                            aria-current={number === model.page ? 'page' : undefined}
                                            disabled={model.loading}
                                            className={`inline-flex h-8 min-w-8 items-center justify-center rounded-md border px-2 text-sm font-medium disabled:opacity-40 ${number === model.page
                                                ? 'border-k-primary bg-k-accent-soft text-k-accent-ink'
                                                : 'border-k-line bg-k-surface text-k-ink-2 hover:bg-k-hover'}`}
                                            onClick={() => model.setPage(number)}
                                        >
                                            {number}
                                        </button>
                                    ))}
                                    <button
                                        type="button"
                                        aria-label="Página siguiente"
                                        disabled={model.page >= model.totalPages || model.loading}
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-k-line bg-k-surface text-k-ink-2 hover:bg-k-hover disabled:opacity-40"
                                        onClick={() => model.setPage(model.page + 1)}
                                    >
                                        <ChevronRightIcon fontSize="small" />
                                    </button>
                                    <Select<number>
                                        size="small"
                                        value={model.pageSize}
                                        disabled={model.loading}
                                        onChange={(event) => model.changePageSize(Number(event.target.value))}
                                        inputProps={{ 'aria-label': 'Registros por página' }}
                                        className="ml-1 h-8 min-w-[72px] rounded-md bg-k-surface text-sm text-k-ink [&_.MuiOutlinedInput-notchedOutline]:border-k-line [&_.MuiSelect-select]:min-h-0 [&_.MuiSelect-select]:py-0 [&_.MuiSelect-select]:leading-8 [&_.MuiSelect-select]:text-k-ink [&_.MuiSvgIcon-root]:text-k-muted"
                                        MenuProps={{
                                            slotProps: {
                                                paper: {
                                                    className: 'rounded-md border border-k-line bg-k-surface text-k-ink [&_.MuiMenuItem-root]:text-k-ink [&_.MuiMenuItem-root.Mui-selected]:bg-k-accent-soft [&_.MuiMenuItem-root.Mui-selected]:text-k-accent-ink',
                                                },
                                            },
                                        }}
                                    >
                                        {PAGE_SIZES.map((size) => (
                                            <MenuItem key={size} value={size}>{size}</MenuItem>
                                        ))}
                                    </Select>
                                </div>
                            </div>
                        </section>
                    </div>
                )}
            </div>
        </KarteraAdminTemplate>
    )
}

export default EmailTemplateList
